/**
 * "Trabalho" — the projects open on this machine, at the top of the sidebar, like a folder opened
 * in Cursor: Trabalho › DevPulse, with its files a click away.
 *
 * "Novo projeto" opens Iniciar projeto (pages/StartProject.tsx), from an idea to a new repository,
 * in both the browser and the desktop app.
 *
 * Desktop: "Abrir projeto" picks a folder with the native dialog; "Adicionar pasta ao workspace"
 * adds one to the workspace the Explorer shows. A project that is a git repository is recognised:
 * when the organization already has it linked, the app remembers its folder and makes it the
 * active repository; when it does not, the row offers to link it in one click (which also creates
 * its project). A file clicked here opens in the Editor, a Markdown one in the reader.
 *
 * Browser: no folder on this machine can be opened from a page, so the item says that it needs
 * the desktop app — the web already shows the linked repositories' files in the Editor.
 */

import { openNewWindow } from '../lib/appWindows'
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { PROJECT_COMMAND_EVENT, type ProjectCommand } from '../lib/menuCommands'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  ChevronRight,
  File,
  FileText,
  Folder,
  FolderGit2,
  FolderOpen,
  FolderPlus,
  Link2,
  MoreHorizontal,
  Plus,
  X,
  AppWindow,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { atLeast, getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { useActiveRepository } from '../lib/activeRepository'
import { folderFor, rememberFolder } from '../lib/repoFolders'
import { buildTree, type TreeNode } from '../lib/repoSource'
import { isMarkdown } from '../lib/markdownFile'
import type { OpenFile } from '../lib/files'
import type { RepositoryLink } from '../lib/types'
import {
  isDesktop,
  repoFiles,
  repoRead,
  repoWrite,
  workspaceAdd,
  workspaceFolders,
  workspaceList,
  workspaceRead,
  workspaceRemove,
  workspaceWrite,
} from '../lib/desktop'
import {
  dismissLinkOffer,
  expandedProjects,
  linkOfferDismissed,
  matchRepository,
  requestOpenInEditor,
  samePath,
  setProjectExpanded,
  useOpenProjects,
  type OpenProject,
} from '../lib/openProjects'
import { FileMenu, WorkspaceFolder, type OpenAs } from './Explorer'
import { useFloating } from './useFloating'

export function ProjectsSidebar() {
  return isDesktop() ? <DesktopProjects /> : <WebOpenProject />
}

// ------------------------------------------------------------------------------ browser

function WebOpenProject() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  return (
    <div className="work work--web">
      <div className="work__start">
        <Link to="/start-project" className="nav-item work__open work__new" title={t('openProjects.newProjectHint')}>
          <Plus size={16} />
          <span>{t('openProjects.newProject')}</span>
        </Link>
        <span className="work__dot" aria-hidden>·</span>
        <button type="button" className="nav-item work__open" aria-expanded={open} onClick={() => setOpen(!open)} title={t('openProjects.openProject')}>
          <FolderOpen size={16} />
          <span>{t('openProjects.openShort')}</span>
        </button>
      </div>
      {open && (
        <div className="work__note tiny" role="note">
          <p>{t('openProjects.desktopOnly')}</p>
          <Link to="/agents/connect?tab=desktop" onClick={() => setOpen(false)}>{t('openProjects.getDesktop')}</Link>
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------------------------------ desktop

/** Everything that reads "the repository" reloads when it changes, as on the Repositories screen. */
const REPOSITORY_QUERIES = ['repositories', 'repository-projects', 'repo-files', 'repo-branches', 'work-orders', 'prompt-queue']

function DesktopProjects() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { projects, open, close } = useOpenProjects()
  const { linked, active, choose, loaded } = useActiveRepository()
  const [problem, setProblem] = useState<string | null>(null)
  const [folded, setFolded] = useState(false)
  const navigate = useNavigate()
  // A file clicked here goes to the Editor through the hand-off in lib/openProjects.ts.
  const openInEditor = useCallback<OpenInEditor>((file, as) => {
    requestOpenInEditor(file, as)
    navigate('/project/editor')
  }, [navigate])
  const { data: folders } = useQuery({ queryKey: ['workspace-folders'], queryFn: workspaceFolders })
  // Workspace folders that are not open projects themselves: added with "Adicionar pasta".
  const extra = (folders ?? []).filter((f) => !projects.some((p) => samePath(p.path, f.path)))

  const reloadRepositories = () => {
    for (const key of REPOSITORY_QUERIES) void queryClient.invalidateQueries({ queryKey: [key] })
  }

  /** A git project the organization already has: its folder remembered, and (when asked) made active. */
  const recognise = (project: OpenProject, makeActive: boolean) => {
    const repository = matchRepository(linked, project)
    if (!repository) return
    rememberFolder(repository.name, project.path)
    if (makeActive) choose(repository.id)
    reloadRepositories()
  }

  const openProject = async () => {
    setProblem(null)
    try {
      const project = await open()
      if (!project) return
      setProjectExpanded(project.path, true)
      void queryClient.invalidateQueries({ queryKey: ['workspace-folders'] })
      if (project.git) recognise(project, true)
    } catch {
      setProblem(t('openProjects.error.open'))
    }
  }

  const addFolder = async () => {
    setProblem(null)
    try {
      await workspaceAdd()
      void queryClient.invalidateQueries({ queryKey: ['workspace-folders'] })
    } catch {
      setProblem(t('openProjects.error.open'))
    }
  }

  // Arquivo › Abrir projeto… / Adicionar pasta ao workspace in the menu bar (docs/refactor/46): done here, as the
  // buttons above do it.
  const commands = useRef({ openProject, addFolder })
  commands.current = { openProject, addFolder }
  useEffect(() => {
    const onCommand = (event: Event) => {
      const command = (event as CustomEvent<ProjectCommand>).detail
      if (command === 'open') void commands.current.openProject()
      else if (command === 'addFolder') void commands.current.addFolder()
    }
    window.addEventListener(PROJECT_COMMAND_EVENT, onCommand)
    return () => window.removeEventListener(PROJECT_COMMAND_EVENT, onCommand)
  }, [])

  // Nothing open yet: one quiet way in, rather than an empty section.
  if (projects.length === 0 && extra.length === 0) {
    return (
      <div className="work work--empty">
        <div className="work__start">
          <Link to="/start-project" className="nav-item work__open work__new" title={t('openProjects.newProjectHint')}>
            <Plus size={16} />
            <span>{t('openProjects.newProject')}</span>
          </Link>
          <span className="work__dot" aria-hidden>·</span>
          <button type="button" className="nav-item work__open" onClick={() => void openProject()} title={t('openProjects.openProjectHint')}>
            <FolderOpen size={16} />
            <span>{t('openProjects.openShort')}</span>
          </button>
        </div>
        {problem && <p className="tiny work__problem" role="alert">{problem}</p>}
      </div>
    )
  }

  return (
    <section className={`work nav-group${folded ? '' : ' nav-group--open'}`} aria-label={t('openProjects.title')}>
      <div className="work__head">
        <button type="button" className="nav-group__header work__header" aria-expanded={!folded} onClick={() => setFolded(!folded)}>
          <FolderOpen size={16} />
          <span>{t('openProjects.title')}</span>
          <ChevronDown size={14} className="nav-group__chevron" aria-hidden />
        </button>
        <span className="work__actions">
          <Link to="/start-project" className="btn btn--ghost btn--icon btn--sm" title={t('openProjects.newProject')} aria-label={t('openProjects.newProject')}>
            <Plus size={13} />
          </Link>
          <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => void openProject()} title={t('openProjects.openProject')} aria-label={t('openProjects.openProject')}>
            <FolderOpen size={13} />
          </button>
          <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => void addFolder()} title={t('openProjects.addFolder')} aria-label={t('openProjects.addFolder')}>
            <FolderPlus size={13} />
          </button>
        </span>
      </div>
      <div className="nav-group__items work__items" role="group" aria-label={t('openProjects.title')}>
        {projects.map((project) => (
          <ProjectRow
            key={project.path}
            project={project}
            repository={project.git && loaded ? matchRepository(linked, project) : null}
            activeId={active?.id ?? null}
            onActivate={(id) => {
              rememberFolder(linked.find((r) => r.id === id)?.name ?? project.name, project.path)
              choose(id)
              reloadRepositories()
            }}
            onLinked={(repository) => {
              rememberFolder(repository.name, project.path)
              choose(repository.id)
              reloadRepositories()
            }}
            onClose={() => void close(project.path).catch(() => setProblem(t('openProjects.error.close')))}
            onProblem={setProblem}
            onOpenFile={openInEditor}
          />
        ))}
        {extra.map((folder) => (
          <div key={folder.path} className="work__extra">
            <WorkspaceFolder
              path={folder.path}
              name={folder.name}
              depth={0}
              onRemove={() => void workspaceRemove(folder.path).then(() => queryClient.invalidateQueries({ queryKey: ['workspace-folders'] }))}
              onOpen={(file, as) => openInEditor(file, as)}
              onContext={(event) => event.preventDefault()}
              onProblem={setProblem}
            />
          </div>
        ))}
        {problem && <p className="tiny work__problem" role="alert">{problem}</p>}
      </div>
    </section>
  )
}

type OpenInEditor = (file: OpenFile, as: OpenAs) => void

function ProjectRow({
  project,
  repository,
  activeId,
  onActivate,
  onLinked,
  onClose,
  onProblem,
  onOpenFile,
}: {
  project: OpenProject
  repository: RepositoryLink | null
  activeId: string | null
  onActivate: (id: string) => void
  onLinked: (repository: RepositoryLink) => void
  onClose: () => void
  onProblem: (message: string | null) => void
  onOpenFile: OpenInEditor
}) {
  const { t } = useI18n()
  const [expanded, setExpanded] = useState(() => expandedProjects().some((p) => samePath(p, project.path)))
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [fileMenu, setFileMenu] = useState<{ x: number; y: number; markdown: boolean; open: (as: OpenAs) => void } | null>(null)
  const [offerHidden, setOfferHidden] = useState(() => linkOfferDismissed(project.path))
  const [linking, setLinking] = useState(false)
  const canLink = atLeast(getSession()?.role, 'Developer')
  const isActive = Boolean(repository && repository.id === activeId)

  // A linked repository whose folder this machine did not know yet: now it does.
  useEffect(() => {
    if (repository && !folderFor(repository.name)) rememberFolder(repository.name, project.path)
  }, [repository, project.path])

  // The project switched at the top (docs/refactor/53): its files open here, the others fold away.
  const shownFor = useRef(activeId)
  useEffect(() => {
    if (shownFor.current === activeId) return
    shownFor.current = activeId
    setExpanded(isActive)
    setProjectExpanded(project.path, isActive)
  }, [activeId, isActive, project.path])

  const toggle = () => {
    setExpanded(!expanded)
    setProjectExpanded(project.path, !expanded)
  }

  const context = (event: MouseEvent, name: string, open: (as: OpenAs) => void) => {
    event.preventDefault()
    setFileMenu({ x: event.clientX, y: event.clientY, markdown: isMarkdown(name), open })
  }

  const showOffer = project.git && !repository && canLink && (!offerHidden || linking)

  return (
    <div className={`work__project${isActive ? ' work__project--active' : ''}`}>
      <div className="work__row">
        <button type="button" className="work__toggle" onClick={toggle} aria-expanded={expanded} title={project.path}>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {project.git ? <FolderGit2 size={14} /> : <Folder size={14} />}
          <span className="work__label truncate">{project.name}</span>
          {project.git && project.branch && project.branch !== 'HEAD' && <span className="work__branch tiny muted mono truncate">{project.branch}</span>}
          {isActive && <span className="work__active" title={t('openProjects.activeRepository')} aria-label={t('openProjects.activeRepository')} />}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm work__more"
          aria-haspopup="menu"
          aria-label={t('openProjects.projectMenu', { name: project.name })}
          title={t('openProjects.projectMenu', { name: project.name })}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect()
            setMenu({ x: box.left, y: box.bottom + 4 })
          }}
        >
          <MoreHorizontal size={13} />
        </button>
      </div>

      {showOffer && (
        <LinkOffer
          project={project}
          onLinked={(linked) => {
            setLinking(false)
            onLinked(linked)
          }}
          onDismiss={() => {
            dismissLinkOffer(project.path)
            setOfferHidden(true)
            setLinking(false)
          }}
        />
      )}

      {expanded && (
        <div className="work__tree">
          {project.git ? (
            <GitTree folder={project.path} onContext={context} onProblem={onProblem} onOpenFile={onOpenFile} />
          ) : (
            <FolderTree folder={project.path} onContext={context} onProblem={onProblem} onOpenFile={onOpenFile} />
          )}
        </div>
      )}

      {menu && (
        <ProjectMenu
          x={menu.x}
          y={menu.y}
          project={project}
          canActivate={Boolean(repository) && !isActive}
          canLink={project.git && !repository && canLink && offerHidden}
          onActivate={() => repository && onActivate(repository.id)}
          onLink={() => setLinking(true)}
          onNewWindow={isDesktop() && repository ? () => void openNewWindow(repository.id) : undefined}
          onCloseProject={onClose}
          onDismiss={() => setMenu(null)}
        />
      )}

      {fileMenu && (
        <FileMenu x={fileMenu.x} y={fileMenu.y} markdown={fileMenu.markdown} onPick={(as) => { setFileMenu(null); fileMenu.open(as) }} onClose={() => setFileMenu(null)} />
      )}
    </div>
  )
}

function ProjectMenu({ x, y, project, canActivate, canLink, onActivate, onLink, onNewWindow, onCloseProject, onDismiss }: {
  x: number
  y: number
  project: OpenProject
  canActivate: boolean
  canLink: boolean
  onActivate: () => void
  onLink: () => void
  /** Opens this project in a window of its own (several windows, lib/appWindows.ts); desktop only. */
  onNewWindow?: () => void
  onCloseProject: () => void
  onDismiss: () => void
}) {
  const { t } = useI18n()
  const { ref, position } = useFloating(x, y, onDismiss)
  const pick = (action: () => void) => () => {
    onDismiss()
    action()
  }
  return (
    <div ref={ref} className="bug-menu work__menu" role="menu" style={{ left: position.left, top: position.top }}>
      <div className="work__menu-path tiny muted mono" title={project.path}>{project.path}</div>
      {canActivate && (
        <button type="button" role="menuitem" className="bug-menu__item" onClick={pick(onActivate)}>
          <Check size={14} aria-hidden /> {t('openProjects.setActive')}
        </button>
      )}
      {canLink && (
        <button type="button" role="menuitem" className="bug-menu__item" onClick={pick(onLink)}>
          <Link2 size={14} aria-hidden /> {t('openProjects.link')}
        </button>
      )}
      {onNewWindow && (
        <button type="button" role="menuitem" className="bug-menu__item" onClick={pick(onNewWindow)}>
          <AppWindow size={14} aria-hidden /> {t('openProjects.newWindow')}
        </button>
      )}
      <button type="button" role="menuitem" className="bug-menu__item" onClick={pick(onCloseProject)}>
        <X size={14} aria-hidden /> {t('openProjects.closeProject')}
      </button>
    </div>
  )
}

/** Why the server refused a link, in the reader's language when it is one we know. */
function linkError(t: (key: string) => string, error: unknown): string {
  if (error instanceof ApiError && error.code && ['remote_has_credentials', 'invalid_remote', 'invalid_name', 'invalid_branch', 'required', 'team_required'].includes(error.code)) {
    return t(`repos.error.${error.code}`)
  }
  return t('error.generic')
}

/** "DevPulse is not linked yet — link it?": one click, a team when the organization has teams. */
function LinkOffer({ project, onLinked, onDismiss }: { project: OpenProject; onLinked: (repository: RepositoryLink) => void; onDismiss: () => void }) {
  const { t } = useI18n()
  const { data: teams } = useQuery({ queryKey: ['repository-teams'], queryFn: api.repositories.teams })
  const [teamId, setTeamId] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const needsTeam = (teams ?? []).length > 0

  const link = useMutation({
    mutationFn: () =>
      api.repositories.link({
        remoteUrl: project.remote_url,
        name: project.name,
        defaultBranch: project.branch && project.branch !== 'HEAD' ? project.branch : null,
        teamId: teamId || null,
        // None: the project is created after the repository.
        projectId: null,
      }),
    onSuccess: onLinked,
    onError: (error) => setProblem(linkError(t, error)),
  })

  return (
    <div className="work__offer tiny" role="group" aria-label={t('openProjects.linkOffer', { name: project.name })}>
      <p>{t('openProjects.linkOffer', { name: project.name })}</p>
      {needsTeam && (
        <select className="select select--sm" value={teamId} onChange={(event) => setTeamId(event.target.value)} aria-label={t('repos.team')}>
          <option value="">{t('repos.teamPick')}</option>
          {(teams ?? []).map((team) => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
      )}
      {problem && <p className="work__problem" role="alert">{problem}</p>}
      <div className="work__offer-actions">
        <button type="button" className="btn btn--primary btn--sm" disabled={link.isPending || (needsTeam && !teamId)} onClick={() => { setProblem(null); link.mutate() }}>
          <Link2 size={12} /> {t('repos.linkShort')}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDismiss}>{t('openProjects.notNow')}</button>
      </div>
    </div>
  )
}

type Context = (event: MouseEvent, name: string, open: (as: OpenAs) => void) => void

function fileProblem(t: (key: string) => string, error: unknown): string {
  const text = String(error)
  return text.includes('not-text') ? t('editor.error.not-text') : text.includes('too-large') ? t('editor.error.too-large') : t('editor.error.failed')
}

/** A git project's files, as git lists them — the same tree as the Explorer's repository. */
function GitTree({ folder, onContext, onProblem, onOpenFile }: { folder: string; onContext: Context; onProblem: (message: string | null) => void; onOpenFile: OpenInEditor }) {
  const { t } = useI18n()
  const { data, isLoading, error } = useQuery({ queryKey: ['project-files', folder], queryFn: () => repoFiles(folder), staleTime: 30_000 })
  const [open, setOpen] = useState<Set<string>>(new Set())

  const toggle = (path: string) => setOpen((current) => {
    const next = new Set(current)
    if (next.has(path)) next.delete(path)
    else next.add(path)
    return next
  })

  const openFile = (node: TreeNode, as: OpenAs) => {
    onProblem(null)
    repoRead(folder, node.path)
      .then((content) => onOpenFile({ name: node.name, content, path: `${folder}/${node.path}`, writer: (text) => repoWrite(folder, node.path, text) }, as))
      .catch((failure) => onProblem(fileProblem(t, failure)))
  }

  if (isLoading) return <p className="tiny muted work__hint">{t('common.loading')}</p>
  if (error) return <p className="tiny work__problem">{t('openProjects.error.files')}</p>

  const render = (nodes: TreeNode[], depth: number): JSX.Element => (
    <ul className="explorer__list">
      {nodes.map((node) =>
        node.children ? (
          <li key={node.path}>
            <button type="button" className="explorer__row" style={{ paddingLeft: 6 + depth * 12 }} onClick={() => toggle(node.path)}>
              {open.has(node.path) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <Folder size={13} /> <span className="truncate">{node.name}</span>
            </button>
            {open.has(node.path) && render(node.children, depth + 1)}
          </li>
        ) : (
          <li key={node.path}>
            <button
              type="button"
              className="explorer__row explorer__row--file"
              style={{ paddingLeft: 18 + depth * 12 }}
              onClick={() => openFile(node, isMarkdown(node.name) ? 'read' : 'edit')}
              onContextMenu={(event) => onContext(event, node.name, (as) => openFile(node, as))}
              title={node.path}
            >
              {isMarkdown(node.name) ? <FileText size={13} /> : <File size={13} />} <span className="truncate">{node.name}</span>
            </button>
          </li>
        ),
      )}
    </ul>
  )

  return render(buildTree(data?.files ?? []), 0)
}

/** A folder without git: listed lazily, as the Explorer lists workspace folders. */
function FolderTree({ folder, onContext, onProblem, onOpenFile }: { folder: string; onContext: Context; onProblem: (message: string | null) => void; onOpenFile: OpenInEditor }) {
  const { t } = useI18n()
  const { data: entries, isLoading, error } = useQuery({ queryKey: ['workspace-list', folder], queryFn: () => workspaceList(folder) })

  const openFile = (file: { name: string; path: string }, as: OpenAs) => {
    onProblem(null)
    workspaceRead(file.path)
      .then((content) => onOpenFile({ name: file.name, content, path: file.path, writer: (text) => workspaceWrite(file.path, text) }, as))
      .catch((failure) => onProblem(fileProblem(t, failure)))
  }

  if (isLoading) return <p className="tiny muted work__hint">{t('common.loading')}</p>
  if (error) return <p className="tiny work__problem">{t('openProjects.error.files')}</p>

  return (
    <ul className="explorer__list">
      {(entries ?? []).map((entry) =>
        entry.dir ? (
          <li key={entry.path}>
            <WorkspaceFolder path={entry.path} name={entry.name} depth={0} onOpen={onOpenFile} onContext={onContext} onProblem={onProblem} />
          </li>
        ) : (
          <li key={entry.path}>
            <button
              type="button"
              className="explorer__row explorer__row--file"
              style={{ paddingLeft: 18 }}
              onClick={() => openFile(entry, isMarkdown(entry.name) ? 'read' : 'edit')}
              onContextMenu={(event) => onContext(event, entry.name, (as) => openFile(entry, as))}
              title={entry.path}
            >
              {isMarkdown(entry.name) ? <FileText size={13} /> : <File size={13} />} <span className="truncate">{entry.name}</span>
            </button>
          </li>
        ),
      )}
    </ul>
  )
}
